import { ReadonlyVec2, vec2, vec3 } from 'gl-matrix';
import { POLYGONS, WINDING_ODD, tesselate } from 'tess2-ts';
import { gl } from '../../../Engine';
import { SignedDistanceField } from '../../../core/models/SignedDistanceField';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { QuadRenderer } from '../../../core/render/renderers/QuadRenderer';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { VertexArrayObject } from '../../../core/utils/VertexArrayObject';
import { UniformBoolean } from '../../../core/utils/uniforms/UniformBoolean';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import {
    UniformVec2,
    UniformVec2Type,
} from '../../../core/utils/uniforms/UniformVec2';
import { UniformVec2Array } from '../../../core/utils/uniforms/UniformVec2Array';
import { UniformVec3 } from '../../../core/utils/uniforms/UniformVec3';
import { RotatedBoundingBox } from '../../../data/common/RotatedBoundingBox';
import { ContourSequence } from '../../../data/structure/ContourSequence';
import { ContourSlice } from '../../../data/structure/ContourSlice';
import { ContourTexture } from '../../../data/structure/ContourTexture';

type Buffers = {
    stencil?: VertexArrayObject;
    innerEdges?: VertexArrayObject;
    outerEdges?: VertexArrayObject;
    corners: VertexArrayObject;
};

class Edge {
    private _start: ReadonlyVec2;
    private _end: ReadonlyVec2;

    public constructor(start: ReadonlyVec2, end: ReadonlyVec2) {
        this._start = start;
        this._end = end;
    }

    public get start(): ReadonlyVec2 {
        return this._start;
    }

    public get end(): ReadonlyVec2 {
        return this._end;
    }

    public get direction(): ReadonlyVec2 {
        return vec2.normalize(
            vec2.create(),
            vec2.subtract(vec2.create(), this._end, this._start)
        );
    }
}

/**
 * Class that provides methods to {@link SDFMesh.renderSDF generate} SDFTexture content for {@link ContourTexture} using a mesh based approach.\
 * That is a relatively time consuming operation which involves many steps, performs multiple rendering cycles,
 * does many computations and iterations over the {@link ContourSequence} data.\
 * But it has been the most reliable approach for smooth and consistent 3D SDF generation.
 */
export class SDFMesh {
    /**
     * {@link StencilShader} that is used to render the inside of the {@link ContourTexture} as white and stencil buffer as 1.
     */
    private stencilShader: StencilShader;

    /**
     * {@link EdgeDistanceShader} that is used to render two parallelograms for each {@link Edge}.\
     * It renders parallelograms on both sides of the edge. One inside and the other outside.\
     * Those need to be rendered separately as inside edges only apply to pixels with stencil value 1
     * and outside edges only to pixels with stencil value 0.
     */
    private edgeDistanceShader: EdgeDistanceShader;

    /**
     * {@link CornerDistanceShader} which renders two circles at each vertex point.\
     * One circle for inner distances and another for outer distances.\
     * Corners formed between two line segments that are convex (the angle between two edges > 90 degrees) are not filled by the {@link EdgeDistanceShader}.
     */
    private cornerDistanceShader: CornerDistanceShader;

    /**
     * {@link ConvolutionShader} that will render the distance information along Z-axis.\
     * After planar SDFs have been generated, we render the optimal distance amongst all the slices.
     */
    private convolutionShader: ConvolutionShader;

    public constructor() {
        this.stencilShader = new StencilShader();
        this.edgeDistanceShader = new EdgeDistanceShader();
        this.cornerDistanceShader = new CornerDistanceShader();
        this.convolutionShader = new ConvolutionShader();
    }

    /**
     * Method to render {@link ContourSequence} on the {@link ContourTexture} using a mesh generation approach.\
     * When calling this method {@link ContourTexture} should already cover the correct slices and dimensions
     * to cover the {@link ContourSequence}.
     * @param texture The {@link ContourTexture} that is to be populated.
     * @param sequence The {@link ContourSequence} that is used to populate the texture.
     * @param _slices Array of slices that needs updating. This does currently nothing.
     * @param innerDistance The distance inside the ROI at which point pixels become 1.
     * @param outerDistance The distance outside the ROI at which point pixels become 0.
     * @returns True to indicate that the {@link SignedDistanceField} was successfully populated.
     */
    public renderSDF(
        texture: ContourTexture,
        sequence: ContourSequence,
        _slices: number[] | undefined,
        innerDistance: number,
        outerDistance: number
    ): string {
        let errorMessage = '';
        const viewportBefore = gl.getParameter(gl.VIEWPORT) as number[];
        const previousProgram = gl.getParameter(
            gl.CURRENT_PROGRAM
        ) as WebGLProgram;
        const previousFBO = gl.getParameter(
            gl.FRAMEBUFFER_BINDING
        ) as WebGLFramebuffer | null;
        const bb = texture.fullBoundingBox;
        const textureGrid = texture.textureGrid;
        const scale = vec3.scale(vec3.create(), bb.size, 0.5);
        const xySdf = new SignedDistanceField(texture.sizePx);
        xySdf.clearTexture();

        const planarSize = vec2.fromValues(xySdf.size[0], xySdf.size[1]);
        const sliceFBO = new FrameBufferObject(planarSize);
        sliceFBO.createTextureAttachment(
            gl.COLOR_ATTACHMENT0,
            gl.R8,
            gl.RED,
            gl.UNSIGNED_BYTE
        );
        sliceFBO.createBufferAttachment(
            gl.DEPTH_STENCIL_ATTACHMENT,
            gl.DEPTH24_STENCIL8
        ); //Could possibly use 32f?
        sliceFBO.checkStatus();

        const destFBO = new FrameBufferObject(planarSize);
        destFBO.unbind();

        const quadRenderer = RenderStorage.getRenderer(QuadRenderer);
        const innerDistances =
            innerDistance > 0
                ? vec2.divide(
                      vec2.create(),
                      vec2.fromValues(innerDistance, innerDistance),
                      vec2.fromValues(scale[0], scale[1])
                  )
                : undefined;
        const outerDistances =
            outerDistance > 0
                ? vec2.divide(
                      vec2.create(),
                      vec2.fromValues(outerDistance, outerDistance),
                      vec2.fromValues(scale[0], scale[1])
                  )
                : undefined;
        gl.clearColor(0, 0, 0, 0);
        gl.viewport(0, 0, planarSize[0], planarSize[1]);

        const params = [gl.STENCIL_TEST, gl.DEPTH_TEST, gl.BLEND];
        const depthMaskBefore = gl.getParameter(gl.DEPTH_WRITEMASK) as boolean;
        gl.depthMask(true);
        const paramsWithEnabledStatus: {
            param: number;
            enabledBefore: boolean;
        }[] = params.map((param) => {
            return { param: param, enabledBefore: gl.isEnabled(param) };
        });

        const textureBottom = textureGrid.getNormalPosition(0);
        for (const contourSlice of sequence.contourSlices) {
            const sliceIndex = Math.round(
                (contourSlice.normalPosition - textureBottom) / textureGrid.resZ
            );
            if (sliceIndex < 0 || sliceIndex >= textureGrid.sizeZ) {
                console.error(
                    `ContourSlice ${contourSlice.normalPosition} is outside of the texture bounds (${textureBottom} - ${textureBottom + textureGrid.sizeZ * textureGrid.resZ})`
                );
                errorMessage += `ContourSlice at ${contourSlice.normalPosition.toFixed(2)} is outside of the texture bounds (${textureBottom} - ${textureBottom + textureGrid.sizeZ * textureGrid.resZ})\n`;
                continue;
            }
            const vaos = this.generateVAOs(
                contourSlice,
                innerDistances,
                outerDistances,
                bb
            );
            sliceFBO.bind();
            if (vaos.stencil) {
                this.renderStencil(vaos.stencil);
            }
            this.renderEdgeDistance(vaos.innerEdges, vaos.outerEdges);
            this.renderCorners(vaos.corners, innerDistances, outerDistances);
            destFBO.bind();
            destFBO.createTextureLayer(xySdf.texture, sliceIndex);
            gl.clear(gl.COLOR_BUFFER_BIT);
            sliceFBO.bindRenderTarget(gl.COLOR_ATTACHMENT0);
            quadRenderer.render();
            destFBO.unbind();
            sliceFBO.unbind();
        }

        this.renderZDistance(
            destFBO,
            texture,
            xySdf,
            innerDistance,
            outerDistance
        );

        paramsWithEnabledStatus.forEach(({ param, enabledBefore }) => {
            if (enabledBefore) {
                gl.enable(param);
            } else {
                gl.disable(param);
            }
        });
        gl.depthMask(depthMaskBefore);

        xySdf.delete();
        sliceFBO.delete();
        destFBO.delete();

        if (previousProgram) {
            gl.useProgram(previousProgram);
        }
        if (previousFBO) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, previousFBO);
        }
        if (viewportBefore) {
            gl.viewport(
                viewportBefore[0],
                viewportBefore[1],
                viewportBefore[2],
                viewportBefore[3]
            );
        }

        return errorMessage;
    }

    /**
     * Method to render contour data using the mesh approach for a single slice
     * @param contours ContourData in 2D space
     * @param sizeMm
     * @param margins
     * @param resolution
     * This method is to be used by drawing tools
     */
    /*public renderSDFForContours(contours: Array<ReadonlyVec2[]>, sizeMm: ReadonlyVec2, margin: number, resolution: number = ContourTexture.DEFAULT_XYRESOLUTION): FrameBufferObject {
        //TODO: This does rounding which is not reflected in Mm. Should perhaps consider that?
        const sizePx = new Vector2f(sizeMm).scale(resolution).ceil();

        const result = new FrameBufferObject(sizePx.x, sizePx.y);
        result.bind();
        result.createTextureAttachment(gl.COLOR_ATTACHMENT0, gl.R8, gl.RED, gl.UNSIGNED_BYTE);
        const depthStencil = result.createBufferAttachment(gl.STENCIL_ATTACHMENT, gl.STENCIL_INDEX8);
        //render content

        const outerDistances = new Vector2f(margins.outerMargin, margins.outerMargin).scale(2).divide(sizeMm);
        const innerDistances = new Vector2f(margins.innerMargin, margins.innerMargin).scale(2).divide(sizeMm);
        gl.clearColor(0, 0, 0, 0);
        gl.viewport(0, 0, sizePx.x, sizePx.y);

        const params = [gl.STENCIL_TEST, gl.BLEND];
        const paramsWithEnabledStatus: { param: number, enabledBefore: boolean }[] = params.map(param => { return { param: param, enabledBefore: gl.isEnabled(param)} });

        let allVaos: {innerEdges: VAO, outerEdges: VAO, corners: VAO}[] = [];
        let stencilArrays: number[][] = [];
        for(const coordinates of contours){
            const contourVAOs = this.sequenceVAOs(coordinates, innerDistances, outerDistances, sizeMm);
            stencilArrays.push(contourVAOs.stencilVertices);
            allVaos.push(contourVAOs);
        }
        let tess = tesselate({windingRule: WINDING_ODD, elementType: POLYGONS, polySize: 3, vertexSize: 2, contours: stencilArrays, strict: false})
        let stencilVAO: VAO | undefined = undefined;
        if(tess){
            stencilVAO = new VAO();
            stencilVAO.bind();
            stencilVAO.createAttribute(0, gl.FLOAT, tess.vertices, 2);
            /**
                 * tess.elements is an array of triangle indices
                 * tess.vertices is an array of vertex coordinates (in somewhat random order)
                 * tess.vertexIndices maps element indices to vertex indices
                 */
    /*stencilVAO.createIndexBuffer(tess.elements);
            this.renderStencil(stencilVAO);
            stencilVAO.delete();
        }
        for(const contour of allVaos){
            this.renderEdgeDistance(contour.innerEdges, contour.outerEdges);
            contour.innerEdges.delete();
            contour.outerEdges.delete();
            this.renderCorners(contour.corners, innerDistances, outerDistances);
            contour.corners.delete();
        }

        result.unbind();
        result.extractRenderTarget(gl.DEPTH_STENCIL_ATTACHMENT);
        depthStencil.delete();

        //this.renderZDistance(destFBO, roi, xySdf, innerDistance, outerDistance);

        paramsWithEnabledStatus.forEach(({ param, enabledBefore }) => {
            if (enabledBefore) {
                gl.enable(param);
            } else {
                gl.disable(param);
            }
        });

        return result;
    }*/

    /**
     * Method to render the stencil buffer.\
     * Pixels within the contour are given stencil value of 1.\
     * Pixels outside the contour are given stencil value of 0.
     * @param vao {@link VertexArrayObject} that contains the triangulated mesh for which the stencil texture is rendered.
     * @param fillColor number that defines grayscale color which the covered pixels should have. Defaults to 1.
     * @returns True to indicate that the stencil map was successfully rendered.
     */
    private renderStencil(vao: VertexArrayObject, fillColor = 1): boolean {
        //render to stencil FBO's stencil attachment.
        gl.enable(gl.STENCIL_TEST);
        gl.enable(gl.DEPTH_TEST);
        gl.depthMask(true);
        gl.stencilMask(0xff);
        gl.clear(
            gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT
        );
        gl.disable(gl.DEPTH_TEST);
        this.stencilShader.start();
        this.stencilShader.fillColor.loadVec3(
            vec3.fromValues(fillColor, fillColor, fillColor)
        );

        gl.stencilFunc(gl.ALWAYS, 1, 0x00); //every fragment passes the stencil test
        gl.stencilOp(gl.INVERT, gl.INVERT, gl.INVERT); //fragment's stencil value is always inverted

        vao.bind(0);
        //render
        gl.drawElements(gl.TRIANGLES, vao.indexCount, gl.UNSIGNED_INT, 0);
        vao.unbind(0);
        vao.delete();
        gl.stencilMask(0x00);
        this.stencilShader.stop();
        return true;
    }

    /**
     * Method to render SDF edge distances on the distance texture.\
     * This renders border as 0.5 and color values inwards and outwards from the edge going linearly towards 1 or 0 respectively.
     * @param innerEdges {@link VertexArrayObject} that presents the mesh data inwards from each edge.
     * @param outerEdges {@link VertexArrayObject} that presents the mesh data outwards from each edge.
     * @returns True to indicate that edges were successfully rendered.
     */
    private renderEdgeDistance(
        innerEdges: VertexArrayObject | undefined,
        outerEdges: VertexArrayObject | undefined
    ): boolean {
        //render to distanceFBO's color attachment 0.
        gl.enable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);

        this.edgeDistanceShader.start();
        if (innerEdges) {
            gl.blendEquation(gl.MIN);
            gl.stencilFunc(gl.EQUAL, 0xff, 0xff);
            innerEdges.bind(0);
            gl.drawElements(
                gl.TRIANGLES,
                innerEdges.indexCount,
                gl.UNSIGNED_INT,
                0
            );
            innerEdges.unbind(0);
            innerEdges.delete();
        }
        if (outerEdges) {
            gl.blendEquation(gl.MAX);
            gl.stencilFunc(gl.EQUAL, 0x00, 0xff);
            outerEdges.bind(0);
            gl.drawElements(
                gl.TRIANGLES,
                outerEdges.indexCount,
                gl.UNSIGNED_INT,
                0
            );
            outerEdges.unbind(0);
            outerEdges.delete();
        }
        this.edgeDistanceShader.stop();
        return true;
    }

    /**
     * Method to render SDF corner distances.\
     * This renders distance relative to each of the contour's corner points
     * @param vao {@link VertexArrayObject} that stores coordinates of a square around each vertex point.
     * @param innerDistance The maximum inner distance in mm, at which point color turns to 1.
     * @param outerDistance The maximum outer distance in mm, at which point color turns to 0.
     * @returns True to indicate that the corners were successfully rendered.
     */
    private renderCorners(
        vao: VertexArrayObject,
        innerDistances: ReadonlyVec2 | undefined,
        outerDistances: ReadonlyVec2 | undefined
    ): boolean {
        this.cornerDistanceShader.start();
        vao.bind(0, 1);
        if (innerDistances) {
            gl.blendEquation(gl.MIN);
            this.cornerDistanceShader.scale.loadVec2(innerDistances);
            gl.stencilFunc(gl.EQUAL, 0xff, 0xff);
            this.cornerDistanceShader.invert.loadBoolean(false);
            gl.drawElements(gl.TRIANGLES, vao.indexCount, gl.UNSIGNED_INT, 0);
        }
        if (outerDistances) {
            gl.blendEquation(gl.MAX);
            this.cornerDistanceShader.scale.loadVec2(outerDistances);
            gl.stencilFunc(gl.EQUAL, 0x00, 0xff);
            this.cornerDistanceShader.invert.loadBoolean(true);
            gl.drawElements(gl.TRIANGLES, vao.indexCount, gl.UNSIGNED_INT, 0);
        }
        vao.unbind(0, 1);
        vao.delete();
        this.cornerDistanceShader.stop();

        gl.disable(gl.DEPTH_TEST);
        gl.disable(gl.STENCIL_TEST);
        return true;
    }

    /**
     * Method to render the optimal distance along the normal direction for each vertex.\
     * This converts planar SDF into a 3D SDF.\
     * Also as this is the final rendering step, it performs the rendering operation on the {@link ContourTexture}'s {@link SignedDistanceField}.
     * @param destFBO {@link FrameBufferObject} on which the {@link destination} {@link ContourTexture}'s {@link ContourTexture.sdf SDF} is bound for rendering.
     * @param texture {@link ContourTexture} on which the {@link source} {@link SignedDistanceField}'s data is rendered.
     * @param innerDistance The maximum inner distance in mm, at which point color turns to 1.
     * @param outerDistance The maximum outer distance in mm, at which point color turns to 0.
     * @returns True to indicate that the distance along z-axis was successfully rendered.
     */
    private renderZDistance(
        destFBO: FrameBufferObject,
        destination: ContourTexture,
        source: SignedDistanceField,
        innerDistance: number,
        outerDistance: number
    ): boolean {
        const sdf = destination.signedDistanceField;
        if (!sdf) {
            throw new Error(
                `Attempt to render ZDistance for ContourTexture without SDF`
            );
        }
        let currentIndex = 0;
        destFBO.bind();

        gl.bindTexture(gl.TEXTURE_3D, source.texture);
        this.convolutionShader.start();
        RenderStorage.getQuadVAO().bind(0);

        const normalOffset = destination.textureGrid.getPosition()[2];
        for (
            let sliceIdx = 0;
            sliceIdx < destination.textureGrid.sizeZ;
            ++sliceIdx
        ) {
            const normalPosition =
                normalOffset + sliceIdx * destination.textureGrid.resZ;
            const innerConvolution: ReadonlyVec2[] = [];
            const outerConvolution: ReadonlyVec2[] = [];
            let relativeDistance: number;

            let distanceMm = 0;
            let k: number;

            let index = currentIndex;
            let within: boolean;
            while (--index >= 0) {
                within = false;
                k = (index + 0.5) / destination.textureGrid.sizeZ;
                const destNormalPosition =
                    normalOffset + index * destination.textureGrid.resZ;
                distanceMm = Math.abs(normalPosition - destNormalPosition);
                if ((relativeDistance = distanceMm / innerDistance) <= 1) {
                    within = true;
                    innerConvolution.push(vec2.fromValues(relativeDistance, k));
                }
                if ((relativeDistance = distanceMm / outerDistance) <= 1) {
                    within = true;
                    outerConvolution.push(vec2.fromValues(relativeDistance, k));
                }
                if (!within) {
                    break;
                }
            }
            index = currentIndex;
            while (++index < destination.textureGrid.sizeZ) {
                within = false;
                const destNormalPosition =
                    normalOffset + index * destination.textureGrid.resZ;
                k = (index + 0.5) / destination.textureGrid.sizeZ;
                distanceMm = Math.abs(destNormalPosition - normalPosition);
                if ((relativeDistance = distanceMm / innerDistance) <= 1) {
                    within = true;
                    innerConvolution.push(vec2.fromValues(relativeDistance, k));
                }
                if ((relativeDistance = distanceMm / outerDistance) <= 1) {
                    within = true;
                    outerConvolution.push(vec2.fromValues(relativeDistance, k));
                }
                if (!within) {
                    break;
                }
            }

            this.convolutionShader.k.loadFloat(
                (currentIndex + 0.5) / destination.textureGrid.sizeZ
            );
            this.convolutionShader.loadConvolutions(
                innerConvolution,
                outerConvolution
            );
            this.convolutionShader.convolutionSize.loadVec2(
                innerConvolution.length,
                outerConvolution.length
            );
            destFBO.createTextureLayer(sdf.texture, currentIndex++);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
        this.convolutionShader.stop();
        gl.bindTexture(gl.TEXTURE_3D, null);
        RenderStorage.getQuadVAO().unbind(0);
        destFBO.unbind();
        return true;
    }

    /**
     * Method to generate the mesh that is to be rendered on the texture slice.\
     * This generates multiple {@link VertexArrayObject}s. Each presenting a mesh required by different rendering stages.
     * @param slice {@link ContourSlice} for which the {@link VertexArrayObject}s are being generated.
     * @param innerDistance {@link ReadonlyVec2} that represents the distance inside the mesh relative to texture size at which point color values become 1.
     * @param outerDistance {@link ReadonlyVec2} that represents the distance outside the mesh relative to texture size at which point color values become 0.
     * @param bb {@link RotatedBoundingBox} representing the dimensions and orientation of the {@link ContourTexture}'s ImageSpace.
     * @returns A collection of {@link VertexArrayObject}s that represent different meshes required in the SDF generation process.
     */
    private generateVAOs(
        slice: ContourSlice,
        innerDistances: ReadonlyVec2 | undefined,
        outerDistances: ReadonlyVec2 | undefined,
        bb: RotatedBoundingBox
    ): Buffers {
        const stencilArrays: number[][] = [];
        let edgeIndex = 0;
        type Builder = {
            vertices: Float32Array;
            indices: Int32Array;
        };
        const numberOfEdges = slice.edgeCount;
        let finalIndex: number = 3 * numberOfEdges;
        const dir = vec2.create();
        let innerBuilder: Builder | undefined = undefined;
        let outerBuilder: Builder | undefined = undefined;
        let handleEdge: (
            edge: Edge,
            edgeIndex: number,
            firstIndex: number,
            finalIndex: number
        ) => void;
        if (innerDistances) {
            innerBuilder = {
                vertices: new Float32Array(numberOfEdges * 9),
                indices: new Int32Array(numberOfEdges * 6),
            };
            if (outerDistances) {
                outerBuilder = {
                    vertices: new Float32Array(numberOfEdges * 9),
                    indices: new Int32Array(numberOfEdges * 6),
                };
                handleEdge = (
                    edge: Edge,
                    edgeIndex: number,
                    firstIndex: number,
                    finalIndex: number
                ): void => {
                    vec2.copy(dir, edge.direction);
                    let vertexIndex = edgeIndex * 9;
                    (outerBuilder as Builder).vertices.set(
                        [edge.start[0], edge.start[1], 0.5],
                        vertexIndex
                    );
                    (innerBuilder as Builder).vertices.set(
                        [edge.start[0], edge.start[1], 0.5],
                        vertexIndex
                    );
                    vertexIndex += 3;
                    (outerBuilder as Builder).vertices.set(
                        [
                            edge.start[0] - dir[1] * outerDistances[0],
                            edge.start[1] + dir[0] * outerDistances[1],
                            0,
                        ],
                        vertexIndex
                    );
                    (innerBuilder as Builder).vertices.set(
                        [
                            edge.start[0] + dir[1] * innerDistances[0],
                            edge.start[1] - dir[0] * innerDistances[1],
                            1,
                        ],
                        vertexIndex
                    );
                    vertexIndex += 3;
                    (outerBuilder as Builder).vertices.set(
                        [
                            edge.end[0] - dir[1] * outerDistances[0],
                            edge.end[1] + dir[0] * outerDistances[1],
                            0,
                        ],
                        vertexIndex
                    );
                    (innerBuilder as Builder).vertices.set(
                        [
                            edge.end[0] + dir[1] * innerDistances[0],
                            edge.end[1] - dir[0] * innerDistances[1],
                            1,
                        ],
                        vertexIndex
                    );

                    const indiceIndex = edgeIndex * 6;
                    const min = edgeIndex * 3;
                    let max = min + 3;
                    if (max === finalIndex) {
                        max = firstIndex;
                    }
                    (innerBuilder as Builder).indices.set(
                        [max, min + 2, min, min, min + 2, min + 1],
                        indiceIndex
                    );
                    (outerBuilder as Builder).indices.set(
                        [min + 2, max, min + 1, min + 1, max, min],
                        indiceIndex
                    );
                };
            } else {
                handleEdge = (
                    edge: Edge,
                    edgeIndex: number,
                    firstIndex: number,
                    finalIndex: number
                ): void => {
                    vec2.copy(dir, edge.direction);
                    let vertexIndex = edgeIndex * 9;
                    (innerBuilder as Builder).vertices.set(
                        [edge.start[0], edge.start[1], 0.5],
                        vertexIndex
                    );
                    vertexIndex += 3;
                    (innerBuilder as Builder).vertices.set(
                        [
                            edge.start[0] - innerDistances[0] * dir[1],
                            edge.start[1] + innerDistances[1] * dir[0],
                            1,
                        ],
                        vertexIndex
                    );
                    vertexIndex += 3;
                    (innerBuilder as Builder).vertices.set(
                        [
                            edge.end[0] - innerDistances[0] * dir[1],
                            edge.end[1] + innerDistances[1] * dir[0],
                            1,
                        ],
                        vertexIndex
                    );

                    const indiceIndex = edgeIndex * 6;
                    const min = edgeIndex * 3;
                    let max = min + 3;
                    if (max === finalIndex) {
                        max = firstIndex;
                    }
                    (innerBuilder as Builder).indices.set(
                        [max, min + 2, min, min, min + 2, min + 1],
                        indiceIndex
                    );
                };
            }
        } else {
            outerBuilder = {
                vertices: new Float32Array(numberOfEdges * 9),
                indices: new Int32Array(numberOfEdges * 6),
            };
            handleEdge = (
                edge: Edge,
                edgeIndex: number,
                firstIndex: number,
                finalIndex: number
            ): void => {
                vec2.copy(dir, edge.direction);
                let vertexIndex = edgeIndex * 9;
                (outerBuilder as Builder).vertices.set(
                    [edge.start[0], edge.start[1], 0.5],
                    vertexIndex
                );
                vertexIndex += 3;
                (outerBuilder as Builder).vertices.set(
                    [
                        edge.start[0] + (outerDistances as vec2)[0] * dir[1],
                        edge.start[1] - (outerDistances as vec2)[1] * dir[0],
                        0,
                    ],
                    vertexIndex
                );
                vertexIndex += 3;
                (outerBuilder as Builder).vertices.set(
                    [
                        edge.end[0] + (outerDistances as vec2)[0] * dir[1],
                        edge.end[1] - (outerDistances as vec2)[1] * dir[0],
                        0,
                    ],
                    vertexIndex
                );

                const indiceIndex = edgeIndex * 6;
                const min = edgeIndex * 3;
                let max = min + 3;
                if (max === finalIndex) {
                    max = firstIndex;
                }
                (outerBuilder as Builder).indices.set(
                    [min + 2, max, min + 1, min + 1, max, min],
                    indiceIndex
                );
            };
        }

        let index4 = 0;
        let index6 = 0;
        let index8 = 0;
        const cornerIndices = new Int32Array(slice.vertexCount * 6);
        const cornerVertexCoordinates = new Float32Array(slice.vertexCount * 8);
        const cornerDirections = new Float32Array(slice.vertexCount * 8);

        let firstIndex = 0;
        const transformedVertex = vec3.create();

        for (const contour of slice.contours) {
            let i2 = 0;
            finalIndex = firstIndex + 3 * contour.edgeCount;
            const stencilVertices = new Array<number>(contour.vertexCount * 2);
            let first: vec2 | undefined = undefined;
            let previous: vec2 | undefined = undefined;
            for (const vertex of contour) {
                vec3.transformMat3(transformedVertex, vertex, bb.invRotation);
                transformedVertex[0] =
                    (transformedVertex[0] - bb.center[0]) / (bb.size[0] * 0.5);
                transformedVertex[1] =
                    (transformedVertex[1] - bb.center[1]) / (bb.size[1] * 0.5);
                /*if(!bb.isPointInside(transformedVertex)){
                    throw new Error(`Point ${vertex[0]}, ${vertex[1]}, ${vertex[2]}, rotated to ${transformedVertex[0]}, ${transformedVertex[1]}, ${transformedVertex[2]} is not within Bb`)
                }*/
                //vec3.transformMat4(transformedVertex, vertex, transformation)
                if (
                    transformedVertex[0] < -1 ||
                    transformedVertex[0] > 1 ||
                    transformedVertex[1] < -1 ||
                    transformedVertex[1] > 1
                ) {
                    throw new Error(
                        `Critical error in SDFMesh generation. TransformedVertex {${transformedVertex[0]}, ${transformedVertex[1]}} is not within texture`
                    );
                }
                stencilVertices[i2++] = transformedVertex[0];
                stencilVertices[i2++] = transformedVertex[1];

                if (previous) {
                    handleEdge(
                        new Edge(
                            previous,
                            vec2.fromValues(
                                transformedVertex[0],
                                transformedVertex[1]
                            )
                        ),
                        edgeIndex++,
                        firstIndex,
                        finalIndex
                    );
                    vec2.set(
                        previous,
                        transformedVertex[0],
                        transformedVertex[1]
                    );
                } else {
                    previous = vec2.fromValues(
                        transformedVertex[0],
                        transformedVertex[1]
                    );
                    first = vec2.clone(previous);
                }

                cornerIndices.set(
                    [
                        index4,
                        index4 + 1,
                        index4 + 2,
                        index4 + 2,
                        index4 + 1,
                        index4 + 3,
                    ],
                    index6
                );
                index6 += 6;
                index4 += 4;
                cornerDirections.set(
                    [-1.0, 1.0, -1.0, -1.0, 1.0, 1.0, 1.0, -1.0],
                    index8
                );
                cornerVertexCoordinates.set(
                    [
                        transformedVertex[0],
                        transformedVertex[1],
                        transformedVertex[0],
                        transformedVertex[1],
                        transformedVertex[0],
                        transformedVertex[1],
                        transformedVertex[0],
                        transformedVertex[1],
                    ],
                    index8
                );
                index8 += 8;
            }
            if (previous && first) {
                handleEdge(
                    new Edge(previous, first),
                    edgeIndex++,
                    firstIndex,
                    finalIndex
                );
            }
            stencilArrays.push(stencilVertices);
            firstIndex = finalIndex;
        }
        const tess = tesselate({
            windingRule: WINDING_ODD,
            elementType: POLYGONS,
            polySize: 3,
            vertexSize: 2,
            contours: stencilArrays,
            strict: false,
        });
        let stencilVAO: VertexArrayObject | undefined = undefined;
        if (tess) {
            stencilVAO = new VertexArrayObject();
            stencilVAO.bind();
            stencilVAO.createAttribute(0, gl.FLOAT, tess.vertices, 2);
            /**
             * tess.elements is an array of triangle indices
             * tess.vertices is an array of vertex coordinates (in somewhat random order)
             * tess.vertexIndices maps element indices to vertex indices
             */
            stencilVAO.createIndexBuffer(tess.elements);
        }

        let innerVAO: VertexArrayObject | undefined = undefined;
        let outerVAO: VertexArrayObject | undefined = undefined;
        if (innerBuilder) {
            innerVAO = new VertexArrayObject();
            innerVAO.bind();
            innerVAO.createIndexBuffer(innerBuilder.indices);
            innerVAO.createAttribute(0, gl.FLOAT, innerBuilder.vertices, 3);
            innerVAO.unbind();
        }
        if (outerBuilder) {
            outerVAO = new VertexArrayObject();
            outerVAO.bind();
            outerVAO.createIndexBuffer(outerBuilder.indices);
            outerVAO.createAttribute(0, gl.FLOAT, outerBuilder.vertices, 3);
            outerVAO.unbind();
        }

        const cornerVAO = new VertexArrayObject();
        cornerVAO.bind();
        cornerVAO.createIndexBuffer(cornerIndices);
        cornerVAO.createAttribute(0, gl.FLOAT, cornerVertexCoordinates, 2);
        cornerVAO.createAttribute(1, gl.FLOAT, cornerDirections, 2);
        cornerVAO.unbind();

        return {
            stencil: stencilVAO,
            innerEdges: innerVAO,
            outerEdges: outerVAO,
            corners: cornerVAO,
        };
    }

    /**
     * Method to delete the {@link ShaderProgram shaders} used in the {@link SDFMesh.renderSDF SDFMesh generation process}.\
     * It is important to call this method when {@link SDFMesh} is no longer needed to de-allocate the WebGL memory.
     */
    delete(): void {
        this.stencilShader.delete();
        this.edgeDistanceShader.delete();
        this.cornerDistanceShader.delete();
        this.convolutionShader.delete();
    }
}

/**
 * Used for rendering the stencil map
 */
class StencilShader extends ShaderProgram {
    private static VERTEX_CONTENT = `#version 300 es
    precision highp float;

    in vec2 position;

    void main() {
        gl_Position = vec4(position, 0.0, 1.0);
    }
    `;

    private static FRAGMENT_CONTENT = `#version 300 es
    precision highp float;

    out vec4 color;

    uniform vec3 fillColor;

    void main() {
        color = vec4(fillColor.rgb, 1.0);
    }
    `;

    public readonly fillColor: UniformVec3;

    constructor() {
        super(
            StencilShader.VERTEX_CONTENT,
            StencilShader.FRAGMENT_CONTENT,
            'position'
        );
        this.fillColor = new UniformVec3('fillColor');
        super.storeUniformLocations(this.fillColor);
    }
}

class EdgeDistanceShader extends ShaderProgram {
    private static VERTEX_CONTENT = `#version 300 es
    precision highp float;
    
    in vec3 position;

    out float pass_distance;
    
    void main() {
        gl_Position = vec4(position, 1.0);
        pass_distance = position.z;
        gl_Position.z = 2.0 * abs(position.z - 0.5);
    }
    `;

    private static FRAGMENT_CONTENT = `#version 300 es
    precision highp float;

    in float pass_distance;
    
    out vec4 color;
    
    void main(){
        color = vec4(pass_distance, pass_distance, pass_distance, 1.0);
    }
    `;

    constructor() {
        super(
            EdgeDistanceShader.VERTEX_CONTENT,
            EdgeDistanceShader.FRAGMENT_CONTENT,
            'position'
        );
    }
}

class CornerDistanceShader extends ShaderProgram {
    private static VERTEX_CONTENT = `#version 300 es
    precision highp float;
    in vec2 position;
    in vec2 direction;
    //scale relative to image size
    uniform vec2 scale;
    out vec2 pass_distance;
    void main() {
        gl_Position = vec4(position + direction * scale, 1.0, 1.0);
        pass_distance = direction;
    }
    `;

    private static FRAGMENT_CONTENT = `#version 300 es
    precision highp float;
    in vec2 pass_distance;
    out vec4 color;
    uniform bool invert;
    void main() {
        float distance = length(pass_distance);
        if(distance >= 1.0){
            discard;
        }
        gl_FragDepth = distance;
        float colorValue = invert ? 0.5 - 0.5 * distance : 0.5 + 0.5 * distance;
        color = vec4(colorValue, colorValue, colorValue, 1.0);
    }
    `;

    public scale: UniformVec2;
    public invert: UniformBoolean;

    constructor() {
        super(
            CornerDistanceShader.VERTEX_CONTENT,
            CornerDistanceShader.FRAGMENT_CONTENT,
            'position',
            'direction'
        );
        this.scale = new UniformVec2('scale');
        this.invert = new UniformBoolean('invert');
        this.storeUniformLocations(this.scale, this.invert);
    }
}

class ConvolutionShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
    
    precision highp float;

    in vec2 position;

    out vec2 center; //screenPosition of the pixel being processed [0, 1]

    void main() {
        gl_Position = vec4(position, 0.0, 1.0);
        center = vec2(position.x + 1.0, position.y + 1.0) * 0.5; // {0 - 1, 0 - 1}
    }
    `;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
    
    precision highp float;
    precision highp sampler3D;

    in vec2 center;

    out vec4 color;

    uniform sampler3D sampler;
    uniform float k;

    //vec2(distMm/maxDistMm, k) where k is textureCoordinate [0, 1].
    uniform vec2 innerConvolution[100];
    uniform vec2 outerConvolution[100];

    //ivec2(inner.length, outer.length) the actual length, most likely < 50
    uniform ivec2 convolutionSize;

    float getOptimal(float original, float relativeDistance, float layerK, bool within){
        float r = texture(sampler, vec3(center, layerK)).r;
        float distance;
        if(within == r > 0.5){
            //on the same side
            distance = length(vec2(2.0 * abs(r - 0.5), relativeDistance));
        } else {
            distance = relativeDistance;
        }
        return min(distance, original);
    }

    void main(){
        vec4 original = texture(sampler, vec3(center, k));
        bool within = original.r > 0.5;
        float optimal;
        if(within){
            optimal = (original.r - 0.5) * 2.0;
            for(int i = 0; i < convolutionSize.x; ++i){
                vec2 element = innerConvolution[i];
                if(element.x >= optimal){
                    continue;
                }
                optimal = getOptimal(optimal, element.x, element.y, within);
            }
        } else {
            optimal = (0.5 - original.r) * 2.0;
            for(int i = 0; i < convolutionSize.y; ++i){
                vec2 element = outerConvolution[i];
                if(element.x >= optimal){
                    continue;
                }
                optimal = getOptimal(optimal, element.x, element.y, within);
            }
        }
        float result = within ? 0.5 + optimal * 0.5 : 0.5 - optimal * 0.5;
        color = vec4(result, result, result, original.a);
    }
    `;

    public k: UniformFloat;
    public innerConvolution: UniformVec2Array;
    public outerConvolution: UniformVec2Array;
    public convolutionSize: UniformVec2;

    public constructor() {
        super(
            ConvolutionShader.VERTEX_CONTENT,
            ConvolutionShader.FRAGMENT_CONTENT,
            'position'
        );
        this.k = new UniformFloat('k');
        this.innerConvolution = new UniformVec2Array('innerConvolution');
        this.outerConvolution = new UniformVec2Array('outerConvolution');
        this.convolutionSize = new UniformVec2(
            'convolutionSize',
            UniformVec2Type.INTEGER
        );
        super.storeUniformLocations(this.k, this.convolutionSize);
    }

    public loadConvolutions(inner: ReadonlyVec2[], outer: ReadonlyVec2[]) {
        if (this.programID) {
            this.innerConvolution.loadVectors(this.programID, inner);
            this.outerConvolution.loadVectors(this.programID, outer);
        }
    }
}
